import { useCallback, useEffect, useRef, useState } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ABI } from '../utils/contractABI';
import { CONTRACT_ADDRESSES, BLACKLIST_POLL_MS } from '../utils/constants';
import { emptyList, fetchBlacklist, isBlacklisted } from '../utils/blacklist';

export const useBlacklist = (account, provider, network) => {
  const [list, setList] = useState(emptyList());
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const contractAddress = network?.chainId === '0x15EB'
    ? CONTRACT_ADDRESSES.TESTNET
    : CONTRACT_ADDRESSES.MAINNET;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBlacklist();
      setList(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll blacklist while mounted
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchBlacklist();
      if (!cancelled) setList(data);
    };
    tick();
    timerRef.current = setInterval(tick, BLACKLIST_POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Look up on-chain user id for the connected account
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!account || !provider || !contractAddress) {
        setUserId(null);
        return;
      }
      try {
        const contract = new ethers.Contract(contractAddress, CONTRACT_ABI, provider);
        const raw = await contract.getUserIdByAddress(account);
        if (cancelled) return;
        const id = raw && raw.toString() !== '0' ? raw.toString() : null;
        setUserId(id);
      } catch (err) {
        if (!cancelled) setUserId(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [account, provider, contractAddress]);

  const status = isBlacklisted(list, account, userId);

  return {
    list,
    userId,
    loading,
    isBlacklisted: status.blocked,
    reason: status.reason || null,
    refresh,
  };
};
